import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import FreeTrialModal from '../modals/FreeTrialModal';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'About Us', path: '/about' },
    { 
      name: 'Solutions', 
      path: '#',
      dropdown: [
        { name: 'Performance Insights', path: '/performance-insights' },
        { name: 'Benchmark Intelligence', path: '/benchmark-intelligence' },
        { name: 'Impact Intelligence', path: '/impact-intelligence' },
        { name: 'Professor of Practice', path: '/pop' },
        { name: 'Capstone Project Management', path: '/pop/capstone' },
        { name: 'Digital Maturity', path: '/digital-maturity', isNew: true },
        { name: 'Data Maturity', path: '/data-maturity', isNew: true },
        { name: 'AI Maturity', path: '/ai-maturity', isNew: true },
        { name: 'Sustainability/ESG', path: '/sustainability', isNew: true },
        { name: 'BPCM Assessment', path: '/bpcm-assessment', isNew: true },
        { name: 'KPI Scorecard', path: '/kpi-scorecard', isNew: true },
        { name: 'AGI Assessment', path: '/multi-agent-dashboard', isNew: true },
        { name: 'Cyber Security', path: '/cyber-security', isNew: true },
        { name: 'SCOR Diagnostic', path: '/diagnostic', isNew: true },
      ]
    },
    { name: 'Resources', path: '/resources' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      <FreeTrialModal isOpen={showTrialModal} onClose={() => setShowTrialModal(false)} />
      
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                <span className="text-white font-bold text-xl">S</span>
              </div>
              <span className={`text-2xl font-bold tracking-tight transition-colors ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}>
                SAMANVAI
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <div key={link.name} className="relative group">
                  {link.dropdown ? (
                    <button
                      data-testid="navbar-solutions-toggle"
                      className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-emerald-500 ${
                        isScrolled ? 'text-gray-700' : 'text-white'
                      }`}
                      onMouseEnter={() => setIsProductsOpen(true)}
                      onMouseLeave={() => setIsProductsOpen(false)}
                    >
                      {link.name}
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      className={`text-sm font-medium transition-colors hover:text-emerald-500 ${
                        isScrolled ? 'text-gray-700' : 'text-white'
                      } ${location.pathname === link.path ? 'text-emerald-500' : ''}`}
                    >
                      {link.name}
                    </Link>
                  )}
                  {link.dropdown && (
                    <div 
                      className={`absolute top-full left-0 mt-2 w-60 max-h-[calc(100dvh-7rem)] overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-100 transition-all duration-200 ${
                        isProductsOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                      }`}
                      onMouseEnter={() => setIsProductsOpen(true)}
                      onMouseLeave={() => setIsProductsOpen(false)}
                    >
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                          data-testid={item.path === '/diagnostic' ? 'nav-scor-diagnostic-desktop' : item.path === '/pop/capstone' ? 'nav-capstone-desktop' : item.path === '/pop' ? 'nav-pop-desktop' : undefined}
                        >
                          {item.name}
                          {item.isNew && (
                            <span className="bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse">
                              NEW
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="hidden lg:flex items-center gap-4">
              <Link to="/solution-provider">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  Become a Solution Provider
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="ghost" className={`${
                  isScrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white hover:text-emerald-300'
                }`}>
                  Book a Demo
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="navbar-mobile-menu-toggle"
              aria-label="Toggle main navigation"
              aria-expanded={isMobileMenuOpen}
              className="lg:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className={`w-6 h-6 ${isScrolled ? 'text-gray-900' : 'text-white'}`} />
              ) : (
                <Menu className={`w-6 h-6 ${isScrolled ? 'text-gray-900' : 'text-white'}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden bg-white border-t transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-[calc(100dvh-5rem)] overflow-y-auto' : 'max-h-0 overflow-hidden'
          }`}
        >
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <div key={link.name}>
                {link.dropdown ? (
                  <div className="space-y-2">
                    <span className="block py-2 text-gray-900 font-medium">{link.name}</span>
                    <div className="pl-4 space-y-1">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className="block py-2 text-gray-600 hover:text-emerald-600"
                          data-testid={item.path === '/diagnostic' ? 'nav-scor-diagnostic-mobile' : item.path === '/pop/capstone' ? 'nav-capstone-mobile' : item.path === '/pop' ? 'nav-pop-mobile' : undefined}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    className="block py-2 text-gray-900 hover:text-emerald-600"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            <div className="pt-4 space-y-2">
              <Link to="/company-intelligence" onClick={() => setIsMobileMenuOpen(false)} data-testid="mobile-nav-company-intelligence-btn" className="block py-2 font-bold text-teal-600">
                Company Intelligence <span className="text-[9px] font-bold text-amber-500 border border-amber-400 rounded-full px-1.5 py-px ml-1">NEW</span>
              </Link>
              <Link to="/solution-provider" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">Become a Solution Provider</Button>
              </Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full">Book a Demo</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
